import {
  type ReactNode,
  useState,
  useCallback,
  useRef,
  useEffect,
} from "react";
import { Spinner } from "../Feedback/Spinner";
import { useMarkDialogDirty } from "../Dialog/FormDirtyContext";
import { useIsFormItemError } from "../FormLayout/FormItemErrorProvider";
import { useControlBarSlot } from "../PageLayout/Internal/ControlBarContext";
import { useControlPresentation } from "./Internal/useControlPresentation";
import {
  ADORNMENT_COLOR,
  inputBaseStyle,
  underlineWrapper,
  boxedBase,
  boxedWrapper,
} from "./Internal/textInputStyles";
import {
  inputWidthClasses as widthClasses,
  type InputWidth,
} from "./inputWidth";

interface NumberInputProps {
  value?: number | undefined;
  setValue: (value: number) => void;
  min?: number | undefined;
  max?: number | undefined;
  placeholder?: string | undefined;
  disabled?: boolean | undefined;
  width?: InputWidth | undefined;
  startAdornment?: ReactNode;
  endAdornment?: ReactNode;
  loading?: boolean | undefined;
  decimal?: boolean | undefined;
  /** Maximum number of decimal places allowed */
  maxDecimalPlaces?: number | undefined;
  /** Forwarded to the underlying <input> for test selection. */
  "data-testid"?: string | undefined;
  /** The input's accessible name, for a box with no visible label of its own. */
  "aria-label"?: string | undefined;
}

// oxlint-disable-next-line complexity, sonarjs/cognitive-complexity -- grandfathered, see ENG-7672
export function NumberInput({
  value,
  setValue,
  min,
  max,
  placeholder,
  disabled,
  width,
  startAdornment,
  endAdornment,
  loading,
  decimal,
  maxDecimalPlaces,
  "data-testid": dataTestid,
  "aria-label": ariaLabel,
}: NumberInputProps) {
  const markDirty = useMarkDialogDirty();
  const [rawValue, setRawValue] = useState(value?.toString() ?? "");
  const [isFocused, setIsFocused] = useState(false);
  // oxlint-disable-next-line fs/no-null -- DOM refs require null
  const inputRef = useRef<HTMLInputElement>(null);

  const formatValue = useCallback(
    (v: number) =>
      maxDecimalPlaces !== undefined
        ? v.toFixed(maxDecimalPlaces)
        : v.toString(),
    [maxDecimalPlaces],
  );

  // Sync rawValue from prop when not focused
  useEffect(() => {
    if (!isFocused && value !== undefined) {
      setRawValue(
        maxDecimalPlaces !== undefined
          ? value.toFixed(maxDecimalPlaces)
          : value.toString(),
      );
    }
  }, [value, isFocused, maxDecimalPlaces]);

  // While focused, show the local string (allows empty). When blurred, show formatted prop value.
  const displayValue = isFocused
    ? rawValue
    : value !== undefined
      ? formatValue(value)
      : rawValue;

  const clamp = useCallback(
    (n: number) => {
      let result = n;
      if (min !== undefined) result = Math.max(min, result);
      if (max !== undefined) result = Math.min(max, result);
      return result;
    },
    [min, max],
  );

  const parse = useCallback(
    (s: string) => (decimal ? parseFloat(s) : parseInt(s, 10)),
    [decimal],
  );

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      // Strip currency formatting so pasted values like "$45.00" or "$1,234.56"
      // populate the field instead of being silently rejected. $, thousands
      // commas, and whitespace are never valid in a number, so stripping them
      // (rather than rejecting the whole paste) is a pure UX win.
      const inputValue = e.target.value.replace(/[$,\s]/g, "");

      // Validate: integer by default (digits and minus only); decimal mode also allows dot
      const pattern = decimal ? /^-?\d*\.?\d*$/ : /^-?\d*$/;
      if (
        !pattern.test(inputValue) &&
        inputValue !== "" &&
        inputValue !== "-"
      ) {
        return;
      }

      // Enforce max decimal places
      if (maxDecimalPlaces !== undefined && inputValue.includes(".")) {
        const decimals = inputValue.split(".")[1];
        if (decimals && decimals.length > maxDecimalPlaces) return;
      }

      setRawValue(inputValue);
      markDirty?.();

      const parsed = parse(inputValue);
      if (!isNaN(parsed)) {
        setValue(clamp(parsed));
      }
    },
    [setValue, clamp, decimal, parse, maxDecimalPlaces, markDirty],
  );

  const handleFocus = useCallback(() => {
    setIsFocused(true);
    // Sync rawValue to current prop value on focus
    if (value !== undefined) {
      setRawValue(value.toString());
    }
  }, [value]);

  const handleBlur = useCallback(() => {
    setIsFocused(false);
    // Clean up incomplete values on blur
    if (rawValue === "" || rawValue === "-" || rawValue === ".") {
      const fallback = min !== undefined ? min : 0;
      setRawValue(formatValue(fallback));
      setValue(fallback);
    } else {
      const parsed = parse(rawValue);
      if (!isNaN(parsed)) {
        setRawValue(formatValue(clamp(parsed)));
      }
    }
  }, [rawValue, min, setValue, formatValue, parse, clamp]);

  const isError = useIsFormItemError();
  // Controls default to a dense boxed field; underline is opt-in (quote/booking).
  const presentation = useControlPresentation();
  const boxed = presentation === "boxed";
  const inControlBar = useControlBarSlot() !== false;
  const compact = !boxed && inControlBar;
  // Inside a filter/control bar the boxed field tightens (h-8 + less padding).
  const boxedCompact = boxed && inControlBar;
  const disabledClasses = disabled ? "opacity-40 pointer-events-none" : "";
  const loadingSpinner = loading ? <Spinner size={14} /> : undefined;
  const effectiveEndAdornment = loadingSpinner ?? endAdornment;

  return (
    <div
      className={
        boxed
          ? `group relative inline-flex items-center ${boxedWrapper(isError, boxedCompact)} ${disabledClasses} ${width ? widthClasses[width] : ""}`
          : `group relative inline-flex items-baseline bg-transparent ${compact ? "pt-1" : "pt-2"} transition-colors ${underlineWrapper(isError)} ${disabledClasses} ${width ? widthClasses[width] : ""}`
      }
    >
      {startAdornment && (
        <span
          className={`flex shrink-0 items-center ${boxed ? `self-center ${boxedCompact ? "pl-1.5" : "pl-3"} ${ADORNMENT_COLOR}` : `self-center pl-0 ${ADORNMENT_COLOR}`}`}
        >
          {startAdornment}
        </span>
      )}
      <input
        ref={inputRef}
        type="text"
        inputMode={decimal ? "decimal" : "numeric"}
        value={displayValue}
        placeholder={placeholder}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        disabled={disabled}
        data-1p-ignore
        aria-label={ariaLabel}
        {...(dataTestid !== undefined && { "data-testid": dataTestid })}
        className={
          boxed
            ? `h-full min-w-0 flex-1 ${boxedCompact ? "px-1.5" : "px-3"} ${boxedBase(false, boxedCompact)} ${startAdornment ? "pl-1.5" : ""}`
            : `min-w-0 flex-1 px-0 ${inputBaseStyle(false, compact)} ${startAdornment ? "pl-2" : ""}`
        }
      />
      {effectiveEndAdornment && (
        <span
          className={`flex shrink-0 items-center ${boxed ? `self-center ${boxedCompact ? "pr-1.5" : "pr-3"} ${ADORNMENT_COLOR}` : `self-center pl-2 ${ADORNMENT_COLOR}`}`}
        >
          {effectiveEndAdornment}
        </span>
      )}
    </div>
  );
}
